import React, { useEffect, useRef } from 'react';

export const CursorLight: React.FC = React.memo(() => {
  const glowRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);

  const isCoarse = typeof window !== 'undefined' && (
    window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 768
  );

  useEffect(() => {
    if (isCoarse) return;

    let rafId: number;
    let x = -100;
    let y = -100;
    let isHovering = false;
    let isMouseDown = false;
    let pending = false;

    const updateDOM = () => {
      const glow = glowRef.current;
      const dot = dotRef.current;

      if (glow && dot) {
        const glowScale = isHovering ? 1.4615 : isMouseDown ? 0.6923 : 1;
        const dotScale = isHovering ? 2 : 1;

        glow.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) scale(${glowScale})`;
        dot.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) scale(${dotScale})`;

        glow.style.background = isHovering
          ? 'radial-gradient(circle, rgba(255, 190, 92, 0.35) 0%, rgba(255, 120, 40, 0.15) 45%, rgba(0,0,0,0) 70%)'
          : 'radial-gradient(circle, rgba(255, 170, 70, 0.22) 0%, rgba(212, 120, 30, 0.08) 50%, rgba(0,0,0,0) 70%)';

        dot.style.boxShadow = isHovering
          ? '0 0 20px 4px rgba(255, 180, 70, 0.9), 0 0 40px 10px rgba(255, 100, 30, 0.6)'
          : '0 0 10px 2px rgba(255, 200, 100, 0.8)';
      }

      pending = false;
    };

    const scheduleUpdate = () => {
      if (!pending) {
        pending = true;
        rafId = requestAnimationFrame(updateDOM);
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      x = e.clientX;
      y = e.clientY;

      const target = e.target as HTMLElement | null;
      isHovering = !!(
        target &&
        (target.tagName === 'BUTTON' ||
          target.tagName === 'A' ||
          target.closest('button') ||
          target.closest('.interactive-light') ||
          target.getAttribute('role') === 'button')
      );

      scheduleUpdate();
    };

    const onMouseDown = () => {
      isMouseDown = true;
      scheduleUpdate();
    };

    const onMouseUp = () => {
      isMouseDown = false;
      scheduleUpdate();
    };

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('mousedown', onMouseDown, { passive: true });
    window.addEventListener('mouseup', onMouseUp, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isCoarse]);

  if (isCoarse) return null;

  return (
    <>
      {/* Primary Candle Torch Glow */}
      <div
        ref={glowRef}
        className="pointer-events-none fixed top-0 left-0 z-50 transition-all duration-100 ease-out rounded-full mix-blend-screen"
        style={{
          transform: 'translate3d(-100px, -100px, 0) translate(-50%, -50%)',
          width: '260px',
          height: '260px',
          background: 'radial-gradient(circle, rgba(255, 170, 70, 0.22) 0%, rgba(212, 120, 30, 0.08) 50%, rgba(0,0,0,0) 70%)',
          filter: 'blur(12px)',
        }}
      />

      {/* Tiny Ember Flame Center Point */}
      <div
        ref={dotRef}
        className="pointer-events-none fixed top-0 left-0 z-50 rounded-full transition-all duration-100 ease-out"
        style={{
          transform: 'translate3d(-100px, -100px, 0) translate(-50%, -50%)',
          width: '6px',
          height: '6px',
          backgroundColor: '#ffda85',
          boxShadow: '0 0 10px 2px rgba(255, 200, 100, 0.8)',
        }}
      />
    </>
  );
});
